import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { layout, system } from 'styled-system';

import Flex from 'shopper/components/Flex';
import Link from 'shopper/components/Link';
import Text from 'shopper/components/Text';

const StyledTextWithEllipsis = styled(Text)`
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;

  img {
    max-width: 100%;
  }

  ${layout}
  ${system({
    maxLines: {
      property: 'WebkitLineClamp',
      defaultScale: ['0', '1', '2', '3', '4', '5', '6'],
    },
    wordBreak: {
      property: 'wordBreak',
    },
  })}
`;

const TextWithEllipsis = ({
  children,
  display,
  maxHeight,
  maxLines,
  withShowMore,
  showMoreProps,
  ...rest
}) => {
  const [isOpen, setOpen] = useState(false);
  const styledTextWithEllipsisRef = useRef(null);
  const [showSeeMoreLink, setShowSeeMoreLink] = useState(false);

  const onSeeMoreLinkButtonClick = () => {
    if (isOpen) {
      styledTextWithEllipsisRef.current.scrollTop = 0;
    }

    setOpen(() => !isOpen);
  };

  useEffect(() => {
    if (withShowMore) {
      setShowSeeMoreLink(
        () =>
          isOpen ||
          styledTextWithEllipsisRef.current?.clientHeight <
            styledTextWithEllipsisRef.current?.scrollHeight
      );
    }
  }, [
    styledTextWithEllipsisRef.current,
    showSeeMoreLink,
    withShowMore,
    isOpen,
  ]);

  if (withShowMore) {
    return (
      <Flex flexDirection="column" position="relative" width="fit-content">
        <StyledTextWithEllipsis
          ref={styledTextWithEllipsisRef}
          display={display}
          isOpen={isOpen}
          maxLines={isOpen ? null : maxLines}
          maxHeight={maxHeight}
          overflowY={
            maxHeight && isOpen && showSeeMoreLink ? 'scroll' : 'hidden'
          }
          {...rest}
        >
          {children}
        </StyledTextWithEllipsis>
        {showSeeMoreLink && (
          <Link
            width="fit-content"
            display={display}
            mt={maxHeight ? 2 : null}
            onClick={onSeeMoreLinkButtonClick}
            {...showMoreProps}
          >
            {isOpen ? 'Ver menos' : 'Ver mais'}
          </Link>
        )}
      </Flex>
    );
  }

  return (
    <StyledTextWithEllipsis
      display={display}
      maxHeight={maxHeight}
      maxLines={maxLines}
      {...rest}
    >
      {children}
    </StyledTextWithEllipsis>
  );
};

TextWithEllipsis.defaultProps = {
  maxLines: 0,
  withShowMore: false,
};

TextWithEllipsis.propTypes = {
  children: PropTypes.node.isRequired,
  maxLines: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
  withShowMore: PropTypes.bool,
};

export default TextWithEllipsis;
